// Машина: её геометрия и сборка в покое.
//
// Картинка барабанов нарисована в квадрате 1920x1920 с пустыми полями. Всё, что на ней лежит —
// окна, разделители, циферблаты манометров, — считается в её координатах, а не в координатах
// макета. Здесь эти числа и живут: их берут и игровое поле, и экран интро.
//
// Игра держит на экране не всю картинку, а полосу с окнами: труба сверху и тележка снизу
// уходят за края экрана. Экран интро показывает машину целиком и потому мельче.

import { Container, Sprite, type Texture } from 'pixi.js';

import { BONUS_COVER_SKELETON, REELS_CONTENT, REELS_GRID, REELS_IMAGE, REEL_ARROW_SKELETON, REEL_COVER_SKELETON, REEL_GEAR_SIZE, REEL_GEAR_SKELETON, arrowIdleAnimation } from '../assets/catalog';
import type { AssetManager, Spine } from '../assets/manager';

import { columnX } from './reels';
import { holdSpine, placeSpine, DESIGN_HEIGHT } from './scene';

/** Ряд окон: где стоит его середина и какой он высоты. */
export interface WindowRow {
  readonly centerY: number;
  readonly height: number;
}

/** Поле 3x3. */
export const MAIN_ROW: WindowRow = {
  centerY: REELS_GRID.main.centerY,
  height: REELS_GRID.main.rows * REELS_GRID.main.stepY,
};

/** Бонусный ряд мини-слота над полем. */
export const BONUS_ROW: WindowRow = {
  centerY: REELS_GRID.bonus.centerY,
  height: REELS_GRID.windowHeight,
};

/** Медные разделители главного поля чуть заходят на верхнюю рамку, закрывая просвет в картинке. */
const MAIN_GEAR_RISE: number = 4;

/**
 * Сколько картинки барабанов остаётся видно над бонусным рядом и под полем.
 *
 * Это рамка машины вокруг крайних окон. Запас взят чуть больше половины окна: меньше — окна
 * упираются в края экрана, больше — машина отъезжает и поле мельчает.
 */
const VIEW_MARGIN: number = 90;

/**
 * Полоса картинки барабанов, которую игровое поле держит на экране целиком.
 *
 * В неё входят бонусный ряд, поле 3x3 и рамка вокруг них. Труба сверху и тележка снизу
 * не входят: на них игрок не смотрит, и ради них поле пришлось бы ужать вдвое.
 * За края экрана они уходят, как в референсе игры.
 */
export const VIEW_TOP: number = BONUS_ROW.centerY - BONUS_ROW.height / 2 - VIEW_MARGIN;
export const VIEW_BOTTOM: number = MAIN_ROW.centerY + MAIN_ROW.height / 2 + VIEW_MARGIN;

/**
 * Точка картинки барабанов, которая встаёт в середину свободного места.
 *
 * По ширине это середина машины, по высоте — середина видимой полосы. От неё считаются
 * координаты всего, что на картинке лежит.
 */
export const ANCHOR_X: number = REELS_CONTENT.x + REELS_CONTENT.width / 2;
export const ANCHOR_Y: number = (VIEW_TOP + VIEW_BOTTOM) / 2;

/** Сколько высоты макета отдано интерфейсу под машиной: кнопке спина и полосе со счётом. */
const UI_HEIGHT: number = 220;

/**
 * Как машина стоит в игре: высота видимой полосы окон и середина этой полосы в макете.
 *
 * Полосе отдано всё место над интерфейсом, поэтому труба и тележка уходят за края экрана.
 */
export const FIELD_VIEW = { height: DESIGN_HEIGHT - UI_HEIGHT, centerY: -UI_HEIGHT / 2 } as const;

/**
 * Как машина стоит на экране интро: та же полоса окон, но дальше от игрока.
 *
 * Там машина показана целиком, поэтому полоса окон ниже игровой: место нужно и трубе,
 * и тележке. Оттуда же она подъезжает к игроку, см. ui/arrival.ts, — обе позы лежат рядом
 * и берутся обоими экранами, иначе машина прыгнула бы в первом кадре подъезда.
 */
export const INTRO_VIEW = { height: 420, centerY: -90 } as const;

/** Переводит точку картинки барабанов в координаты слоя, лежащего на ней. */
export function onImage(x: number, y: number): { x: number; y: number } {
  return { x: x - ANCHOR_X, y: y - ANCHOR_Y };
}

/**
 * Ставит разделители в промежутки между полосами барабанов.
 *
 * Полоса нарисована вокруг начала координат скелета, поэтому она встаёт прямо на середину
 * промежутка. По ширине она приводится к промежутку, по высоте — к высоте ряда, так что
 * в бонусном ряду та же полоса выходит сжатой.
 *
 * Без разделителей в промежутках видно фон: на картинке барабанов там пусто.
 */
export function dividers(reels: number, row: WindowRow, assets: AssetManager): Spine[] {
  const gears: Spine[] = [];
  const gap: number = REELS_GRID.stepX - REELS_GRID.windowWidth;

  for (let index = 1; index < reels; index++) {
    const gear: Spine = assets.spine(REEL_GEAR_SKELETON);
    const center = onImage((columnX(index - 1, reels) + columnX(index, reels)) / 2, row.centerY);

    gear.scale.set(gap / REEL_GEAR_SIZE.width, row.height / REEL_GEAR_SIZE.height);
    // Просвет есть только у двух разделителей 3×3; мини-слот нарисован без него.
    gear.position.set(center.x, center.y - (row === MAIN_ROW ? MAIN_GEAR_RISE : 0));
    gear.state.setAnimation(0, REEL_GEAR_SKELETON.animations.move, true);

    gears.push(gear);
  }

  return gears;
}

/**
 * Стрелка манометра на своём циферблате, поставленная на ноль.
 *
 * Координаты у неё те же, что у картинки барабанов: циферблаты на ней и нарисованы.
 *
 * @param column Номер барабана: под каждым свой циферблат.
 * @throws Error если на картинке машины нарисовано меньше циферблатов, чем нужно манометров
 */
export function gaugeArrow(assets: AssetManager, column: number): Spine {
  const pivot: { readonly x: number; readonly y: number } | undefined = REELS_GRID.gauge.pivots[column];
  if (pivot === undefined)
    throw new Error(`Для манометра ${column} не снята ось на картинке барабанов`);

  const arrow: Spine = assets.spine(REEL_ARROW_SKELETON);

  // Стрелка ходит вокруг начала координат скелета, поэтому оно и встаёт на ось циферблата.
  // Ставить по середине габарита нельзя: габарит задаёт облако пара, а не сама стрелка.
  arrow.scale.set(REELS_GRID.gauge.size / arrow.skeleton.data.width);
  arrow.position.set(pivot.x, pivot.y);
  arrow.state.setAnimation(0, arrowIdleAnimation(0), true);

  return arrow;
}

/** Сколько барабанов нарисовано на машине. Под каждым свой циферблат манометра. */
const COLUMNS: number = REELS_GRID.gauge.pivots.length;

/**
 * Машина в покое: створки закрыты, стрелки манометров стоят на нуле.
 *
 * Такой машина стоит до первого спина, и такой её показывает экран интро. Ничего живого
 * в ней нет: ни барабанов, ни символов, ни счётчика множителя — игрок видит саму машину.
 *
 * Узел ставится серединой видимой полосы в начало своих координат, поэтому двигать его
 * можно как одну картинку.
 *
 * @param height Высота видимой полосы окон в координатах макета. По ней берётся масштаб,
 * поэтому машина в интро и машина на поле мерятся одним числом.
 */
export function stillMachine(assets: AssetManager, height: number): Container {
  const view = new Container();
  const scale: number = height / (VIEW_BOTTOM - VIEW_TOP);

  // Картинка лежит в квадрате с пустыми полями, поэтому якорь стоит на самой машине,
  // а не в центре файла.
  const texture: Texture = assets.texture(REELS_IMAGE);
  const picture = new Sprite(texture);
  picture.anchor.set(ANCHOR_X / texture.width, ANCHOR_Y / texture.height);
  picture.scale.set(scale);

  // Всё, что лежит на машине, живёт в координатах её картинки: слой повторяет её масштаб
  // и стоит на том же якоре.
  const layer = new Container();
  layer.scale.set(scale);

  // Створки бонусного ряда закрывают только окна, поэтому промежутки между ними держат
  // разделители. У поля 3x3 своя заглушка, и она шире всех трёх окон разом.
  layer.addChild(...dividers(COLUMNS, BONUS_ROW, assets));

  for (let column = 0; column < COLUMNS; column++) {
    const sash: Spine = assets.spine(BONUS_COVER_SKELETON);
    const center = onImage(columnX(column, COLUMNS), BONUS_ROW.centerY);

    sash.position.set(center.x, center.y);
    holdSpine(sash, BONUS_COVER_SKELETON.animations.close);

    layer.addChild(sash);
  }

  const cover: Spine = assets.spine(REEL_COVER_SKELETON);
  const field = onImage(REELS_GRID.centerX, MAIN_ROW.centerY);

  placeSpine(cover, 1, field.x, field.y);
  holdSpine(cover, REEL_COVER_SKELETON.animations.close);

  // Стрелки считают координаты по самой картинке, поэтому их узел сдвинут на якорь целиком.
  const gauges = new Container();
  gauges.position.set(-ANCHOR_X, -ANCHOR_Y);

  for (let column = 0; column < COLUMNS; column++)
    gauges.addChild(gaugeArrow(assets, column));

  layer.addChild(cover, gauges);
  view.addChild(picture, layer);

  return view;
}
